import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Disclosure, Fact } from '@/components/RoleModificationPanels';
import { catalogCandidateLines, catalogChoiceField, catalogClarificationView, catalogSelectionReady, catalogStepCopy } from '@/lib/menuChangeView';

const INPUTS = [
  ['target_role', 'Target SAP role', 'e.g. Z_HALO_CAT_SHIP', 'workflow-info-target-role'],
  ['application', 'Fiori application', 'e.g. LSPI Obtain Report', 'workflow-info-application'],
  ['source_configuration', 'Source configuration', 'Confirm the verified tile and target mapping', 'workflow-info-source-configuration'],
  ['catalog', 'Destination catalog', 'Exact SAP catalog ID', 'workflow-info-catalog'],
  ['operation', 'Operation', 'Catalog assignment, content change, or mapping correction', 'workflow-info-operation'],
];

function FieldLabel({ htmlFor, label, presence }) {
  return (
    <Label htmlFor={htmlFor}>
      {label}
      <span className="ml-2 text-xs uppercase tracking-wide text-muted-foreground">
        {presence === 'required' ? 'Required' : 'Optional'}
      </span>
    </Label>
  );
}

function selectChoice(setInfoForm, field, value) {
  setInfoForm((current) => {
    const next = { ...current, [field]: value };
    if (field === 'application') {
      next.source_configuration = '';
      next.access_strategy = '';
      next.copy_source = '';
      next.catalog = '';
    }
    if (field === 'source_configuration') {
      next.access_strategy = '';
      next.copy_source = '';
      next.catalog = '';
    }
    if (field === 'access_strategy') {
      next.copy_source = '';
    }
    return next;
  });
}

const SUMMARY_KNOWN_FIELDS = new Set([
  'application',
  'requested_application',
  'target_role',
  'target_system',
  'target_client',
  'fiori_id',
  'semantic_action',
  'navigation_parameters',
  'source_catalogs',
  'source_catalog',
]);

function optionFor(candidate, details, field) {
  if (field === 'copy_source') {
    const source = details.find((item) => item.copy_source_id === candidate) || {};
    const owner = source.owner_catalog_id ? ` (${source.owner_catalog_id})` : '';
    const local = source.catalog_id ? ` (${source.catalog_id})` : '';
    return {
      value: candidate,
      title: source.catalog_id || candidate,
      summary: source.owner_catalog_id ? `Stable reference owned by ${source.owner_catalog_id}.` : '',
      recommended: false,
      technical: [
        source.difference || '',
        source.tile_id ? `Catalog-local tile${local}: ${source.tile_id}` : '',
        source.target_mapping_id ? `Catalog-local target mapping${local}: ${source.target_mapping_id}` : '',
        source.tile_id_stable ? `Stable tile${owner}: ${source.tile_id_stable}` : '',
        source.target_mapping_id_stable ? `Stable target mapping${owner}: ${source.target_mapping_id_stable}` : '',
        source.navigation_parameters_verified === false
          ? 'Navigation parameters were not returned and remain unverified.'
          : '',
      ],
    };
  }
  const row = details.find((item) => (
    item.configuration_id === candidate
    || item.strategy_id === candidate
    || item.fiori_id === candidate
    || item.catalog_id === candidate
  )) || {};
  if (field === 'application') {
    const identity = row.fiori_id || candidate;
    const distinctTitle = row.title && row.title !== identity ? ` — ${row.title}` : '';
    return {
      value: candidate,
      title: `${identity}${distinctTitle}`,
      summary: row.activity_summary || '',
      recommended: Boolean(row.recommended),
      technical: [
        row.intent ? `Intent: ${row.intent}` : [row.semantic_object, row.semantic_action].filter(Boolean).join('/'),
        row.sapui5_component ? `SAPUI5 component: ${row.sapui5_component}` : '',
        row.technical_id && row.technical_id !== row.sapui5_component ? `Technical id: ${row.technical_id}` : '',
        (row.source_catalogs || []).length ? `Source catalogs: ${row.source_catalogs.join(', ')}` : '',
        row.tile_id ? `Tile: ${row.tile_id}` : '',
        row.target_mapping_id ? `Target mapping: ${row.target_mapping_id}` : '',
      ],
    };
  }
  if (field === 'access_strategy') {
    const strategy = details.find((item) => item.strategy_id === candidate) || {};
    const facts = [
      strategy.catalog_kind_label,
      strategy.function_summary || strategy.summary,
      strategy.already_assigned ? 'Already assigned to target role' : '',
      strategy.role_count_label && strategy.catalog_id
        ? `Roles referencing catalog: ${strategy.role_count_label}`
        : '',
      strategy.additional_access || '',
    ].filter(Boolean);
    return {
      value: candidate,
      title: strategy.title || candidate,
      group: strategy.group || '',
      groupLabel: strategy.group_label || '',
      summary: facts.join(' '),
      facts,
      recommended: false,
      warning: false,
      readiness: strategy.execution_readiness === 'blocked' ? (strategy.execution_label || '') : '',
      limitation: strategy.mapping_limitation || '',
      technical: [
        strategy.operation ? `Operation: ${strategy.operation}` : '',
        strategy.lookup_method ? `Lookup: ${strategy.lookup_method}` : '',
        strategy.role_count_detail || '',
        strategy.equivalent_catalogs?.length ? `Same additional access: ${strategy.equivalent_catalogs.join(', ')}` : '',
        strategy.catalog_master_entry_count
          ? `${strategy.catalog_master_entry_count} catalog master entries are recorded separately from visualization references.`
          : '',
        strategy.page_chip_count
          ? `${strategy.page_chip_count} page chips are recorded separately from visualization references.`
          : '',
        strategy.transport_note || '',
      ],
    };
  }
  if (field === 'source_configuration') {
    const catalogs = row.source_catalogs || [];
    const semantic = [row.semantic_object, row.semantic_action].filter(Boolean).join('/');
    const owner = row.owner_catalog_id ? ` (${row.owner_catalog_id})` : '';
    const localCatalog = catalogs[0] ? ` (${catalogs[0]})` : '';
    return {
      value: candidate,
      title: row.fiori_id ? `${row.fiori_id}${row.title ? ` — ${row.title}` : ''}` : candidate,
      summary: [
        semantic,
        row.metadata_complete === false ? (row.difference || '') : '',
        row.equivalence === 'partial' ? 'Navigation parameters were not returned and remain unverified.' : '',
        catalogs.length && row.metadata_complete !== false ? `Same configuration in ${catalogs.join(', ')}.` : '',
      ].filter(Boolean).join(' '),
      recommended: Boolean(row.recommended),
      warning: row.metadata_complete === false,
      evidenceLabel: row.metadata_complete === false ? 'Evidence incomplete' : '',
      technical: [
        row.equivalence ? `Equivalence: ${row.equivalence}` : '',
        row.suitability || '',
        row.metadata_complete !== false ? (row.difference || '') : '',
        row.tile_id ? `Catalog-local tile${localCatalog}: ${row.tile_id}` : '',
        row.target_mapping_id ? `Catalog-local target mapping${localCatalog}: ${row.target_mapping_id}` : '',
        row.tile_id_stable ? `Stable tile${owner}: ${row.tile_id_stable}` : '',
        row.target_mapping_id_stable ? `Stable target mapping${owner}: ${row.target_mapping_id_stable}` : '',
      ],
    };
  }
  return {
    value: candidate,
    title: row.catalog_id || candidate,
    summary: [row.suitability, row.shared_role_impact].filter(Boolean).join(' '),
    recommended: Boolean(row.recommended),
    technical: [
      row.service_entry_count ? `${row.service_entry_count} service entries are recorded separately from this tile.` : '',
      row.change_kind === 'content_mapping' ? 'This adds a tile to the catalog. It does not assign the catalog to a role.' : '',
    ],
  };
}

function ChoiceCards({
  clarification,
  infoForm,
  setInfoForm,
  canMutate,
  onContinue,
  submitting = false,
  canApprove = false,
  approvalIntent = false,
}) {
  const field = catalogChoiceField(clarification);
  const details = Array.isArray(clarification.candidate_details) ? clarification.candidate_details : [];
  const selected = String(infoForm[field] || '');
  const selectedStrategy = field === 'access_strategy'
    ? details.find((item) => item.strategy_id === selected)
    : null;
  const approveOnSelect = canApprove && (
    selectedStrategy?.operation === 'APPLICATION_TO_CATALOG'
    || (field === 'copy_source' && approvalIntent)
  );
  const summary = clarification.recommendation?.summary || '';
  const copy = catalogStepCopy(clarification, { approveOnSelect });
  const known = Array.isArray(clarification.known) ? clarification.known.filter((row) => row?.value) : [];
  const summaryKnown = known.filter((row) => SUMMARY_KNOWN_FIELDS.has(row.field));
  const technicalKnown = known.filter((row) => !SUMMARY_KNOWN_FIELDS.has(row.field));
  const ready = catalogSelectionReady(clarification, infoForm);
  return (
    <Card data-testid="workflow-catalog-selection">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex flex-wrap items-center gap-2">
          {copy?.title}
          <Badge variant="outline" className={copy?.warning ? 'border-cp-mod text-cp-mod' : undefined}>
            {copy?.warning ? 'Evidence incomplete' : 'Selection Required'}
          </Badge>
        </CardTitle>
        {copy?.description ? <CardDescription>{copy.description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {summaryKnown.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="workflow-catalog-summary">
            {summaryKnown.map((row) => (
              <Fact
                key={`${row.field}-${row.value}`}
                label={row.label || row.field}
                value={row.value}
                mono={row.field !== 'application'}
              />
            ))}
          </div>
        )}
        {technicalKnown.length > 0 && (
          <Disclosure label="Technical configuration" openLabel="Hide technical configuration" testId="workflow-catalog-technical-configuration">
            <div className="grid gap-3 sm:grid-cols-1">
              {technicalKnown.map((row) => (
                <Fact
                  key={`${row.field}-${row.value}`}
                  label={row.label || row.field}
                  value={row.value}
                  mono
                />
              ))}
            </div>
          </Disclosure>
        )}
        {summary ? (
          <div data-testid="workflow-catalog-recommendation">
            <Badge>Recommended</Badge>
            <p className="mt-1">{summary}</p>
          </div>
        ) : null}
        <fieldset className="grid gap-2" aria-label={copy?.title} data-testid="workflow-catalog-candidate-details">
          <ul className="space-y-2">
            {clarification.candidates.map((candidate, index) => {
              const option = optionFor(candidate, details, field);
              const checked = selected === candidate;
              const previous = index > 0 ? optionFor(clarification.candidates[index - 1], details, field) : null;
              const showGroup = Boolean(option.groupLabel) && option.group !== previous?.group;
              return (
                <li key={candidate}>
                  {showGroup ? (
                    <h3 className="mb-2 mt-3 text-sm font-semibold" data-testid={`workflow-strategy-group-${option.group}`}>
                      {option.groupLabel}
                    </h3>
                  ) : null}
                  <label
                    className={`block rounded-md border p-3 ${checked ? 'border-cp-indigo/60 bg-cp-inset' : 'border-cp-line/60'}`}
                    data-testid={`workflow-choice-${candidate}`}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <input
                        type="radio"
                        name="workflow-clarification-choice"
                        value={candidate}
                        checked={checked}
                        disabled={!canMutate}
                        aria-label={`Select ${option.title}`}
                        onChange={() => selectChoice(setInfoForm, field, candidate)}
                      />
                      <span className="font-medium">{option.title}</span>
                      {option.recommended ? <Badge>Recommended</Badge> : null}
                      {checked ? <Badge variant="outline">Selected</Badge> : null}
                      {option.evidenceLabel ? (
                        <Badge variant="outline" className="border-cp-mod text-cp-mod">{option.evidenceLabel}</Badge>
                      ) : null}
                      {option.warning && !option.evidenceLabel ? (
                        <Badge variant="outline" className="border-cp-mod text-cp-mod">Incomplete impact</Badge>
                      ) : null}
                    </span>
                    {option.facts?.length ? option.facts.map((line) => (
                      <p className="mt-1" key={line}>{line}</p>
                    )) : (option.summary ? <p className="mt-1 text-muted-foreground">{option.summary}</p> : null)}
                    {option.impact ? <p className="mt-1">{option.impact}</p> : null}
                    {option.limitation ? <p className="mt-1">{option.limitation}</p> : null}
                    {option.readiness ? <p className="mt-1" data-testid={`workflow-execution-${candidate}`}>{option.readiness}</p> : null}
                    {option.technical.some(Boolean) ? (
                      <div className="mt-2">
                        <Disclosure label="Technical details" openLabel="Hide technical details">
                          {option.technical.filter(Boolean).map((line) => (
                            <p className="text-xs text-muted-foreground" key={line}>{line}</p>
                          ))}
                        </Disclosure>
                      </div>
                    ) : null}
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
        {onContinue ? (
          <Button
            type="button"
            onClick={() => onContinue({
              approve: approveOnSelect,
              catalog: selectedStrategy?.catalog_id || '',
            })}
            disabled={submitting || !canMutate || !ready}
            data-testid="workflow-info-submit"
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
            {approveOnSelect ? 'Approve & apply' : 'Continue'}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function CatalogClarificationFields({
  clarification = {},
  infoForm,
  setInfoForm,
  canMutate,
  onContinue,
  submitting = false,
  canApprove = false,
  approvalIntent = false,
}) {
  const view = catalogClarificationView(clarification);
  const guided = Boolean(catalogStepCopy(clarification));
  const candidates = Array.isArray(clarification.candidates) ? clarification.candidates : [];
  if (guided && candidates.length > 0) {
    return (
      <ChoiceCards
        clarification={clarification}
        infoForm={infoForm}
        setInfoForm={setInfoForm}
        canMutate={canMutate}
        onContinue={onContinue}
        submitting={submitting}
        canApprove={canApprove}
        approvalIntent={approvalIntent}
      />
    );
  }
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {clarification.operation_guidance && (
        <p className="text-sm md:col-span-2" data-testid="workflow-catalog-operation-guidance">
          {clarification.operation_guidance}
        </p>
      )}
      {clarification.recommendation?.reason && (
        <p className="text-sm md:col-span-2" data-testid="workflow-catalog-recommendation">
          {clarification.recommendation.summary || clarification.recommendation.reason}
        </p>
      )}
      {view.known.length > 0 && (
        <div className="grid gap-1 text-sm md:col-span-2" data-testid="workflow-catalog-known">
          {view.known.map((row) => (
            <div key={`${row.field}-${row.value}`}>
              <span className="font-medium">{row.label || row.field}: </span>
              {row.value}
            </div>
          ))}
        </div>
      )}
      {catalogCandidateLines(clarification).length > 0 && (
        <div className="grid gap-1 text-sm md:col-span-2" data-testid="workflow-catalog-candidate-details">
          {catalogCandidateLines(clarification).map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      )}
      {candidates.length > 0 && (
        <div className="flex flex-wrap gap-2 md:col-span-2" data-testid="workflow-catalog-candidates">
          {candidates.map((candidate) => (
            <button
              key={candidate}
              type="button"
              disabled={!canMutate}
              data-testid={`workflow-catalog-candidate-${candidate}`}
              onClick={() => {
                const field = catalogChoiceField(clarification);
                if (field) selectChoice(setInfoForm, field, candidate);
              }}
            >
              {candidate}
            </button>
          ))}
        </div>
      )}
      {INPUTS.filter(([name]) => view[name] === 'required' || view[name] === 'optional').map(([name, label, placeholder, testId]) => (
        <div className="space-y-1.5" key={name}>
          <FieldLabel htmlFor={`wf-info-${name}`} label={label} presence={view[name]} />
          <Input
            id={`wf-info-${name}`}
            value={infoForm[name] || ''}
            onChange={(event) => setInfoForm((current) => ({ ...current, [name]: event.target.value }))}
            placeholder={placeholder}
            disabled={!canMutate}
            data-testid={testId}
            required={view[name] === 'required'}
          />
        </div>
      ))}
      <div className="space-y-1.5 md:col-span-2">
        <FieldLabel htmlFor="wf-info-note" label="Additional information" presence={view.note} />
        <Textarea
          id="wf-info-note"
          value={infoForm.note || ''}
          onChange={(event) => setInfoForm((current) => ({ ...current, note: event.target.value }))}
          placeholder="Add only the identifier HALO asked for"
          disabled={!canMutate}
          data-testid="workflow-info-note"
        />
      </div>
    </div>
  );
}
